import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useDropdowns from 'src/hooks/useDropdowns'
import { AddChapter } from 'src/redux/actions'
import {
  buildSampleRows,
  CONTENT_PATH_DEPTH,
  importRows,
  parseNumber,
  parseStatus,
  requireValues,
  resolveContentPath,
  resolveOptionKey,
} from 'src/util/bulkImport'
import { downloadCsv } from 'src/util/csv'

import BulkUploadPanel from '../helperComponents/BulkUploadPanel'

export const CHAPTER_CSV_HEADERS = [
  'language',
  'course',
  'name',
  'description',
  'difficultyLevel',
  'sortOrder',
  'status',
]

const SAMPLE_CHAPTERS = [
  {
    language: 'English',
    course: 'Basics of Greetings',
    name: 'Greetings Basics',
    description: 'Introductory chapter covering common greetings',
    difficultyLevel: 'Beginner',
    sortOrder: '1',
    status: 'Active',
  },
  {
    language: 'English',
    course: 'Numbers and Counting',
    name: 'Numbers 1 to 20',
    description: 'Chapter covering the numbers one to twenty',
    difficultyLevel: 'Absolute Beginners',
    sortOrder: '2',
    status: 'Active',
  },
  {
    language: 'Spanish',
    course: 'Business Communication',
    name: 'Advanced Grammar',
    description: 'Chapter kept hidden until content review is complete',
    difficultyLevel: 'Advanced Level',
    sortOrder: '3',
    status: 'Inactive',
  },
]

export const SAMPLE_CHAPTER_CSV_ROWS = buildSampleRows(CHAPTER_CSV_HEADERS, SAMPLE_CHAPTERS)

export const downloadSampleChapterCsv = () =>
  downloadCsv('bulk-chapters-sample.csv', SAMPLE_CHAPTER_CSV_ROWS)

// Mirrors the payload AddEditChapter posts.
export const buildChapterPayload = (row, { dropdowns, difficultyLevels }) => {
  requireValues(row, ['name', 'description'])

  return {
    ...resolveContentPath(dropdowns, row, CONTENT_PATH_DEPTH.COURSE),
    name: row.name,
    description: row.description,
    difficultyLevel: resolveOptionKey(difficultyLevels, row.difficultyLevel, 'difficultyLevel', {
      fallback: 'allLevels',
    }),
    sortOrder: parseNumber(row.sortOrder, 'sortOrder'),
    isActive: parseStatus(row.status),
  }
}

const BulkUploadChapter = ({ toggleDrawer, refetch }) => {
  const dispatch = useDispatch()
  const { dropdowns } = useDropdowns()
  const difficultyLevels = useSelector(store => store.mainReducer.courseDifficultyLevel)

  const onImport = useCallback(
    rows =>
      importRows(rows, async row => {
        await dispatch(AddChapter(buildChapterPayload(row, { dropdowns, difficultyLevels })))
      }),
    [dispatch, dropdowns, difficultyLevels]
  )

  return (
    <BulkUploadPanel
      entityPlural="Chapters"
      headers={CHAPTER_CSV_HEADERS}
      onDownloadSample={downloadSampleChapterCsv}
      onImport={onImport}
      toggleDrawer={toggleDrawer}
      refetch={refetch}
      helpText={
        'Each row names its own parents: language must match a Language, and course must match ' +
        'a Course inside that language (name or id in either column). ' +
        'difficultyLevel defaults to All levels when blank; status is Active/Inactive.'
      }
    />
  )
}

export default BulkUploadChapter
